import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { recordCampaignImpression } from '../../actions/user.actions';
import { selectCampaignImpressions, selectIsMeLoaded } from '../../reducers/user.selectors';
import { ampliTrackMessageDisplayedEvent } from '../../services/track.service';
import { useAppDispatch } from '../../store/hooks';
import { type CampaignManager, campaignManager } from './campaignManager';
import { type CampaignId, getCampaignConfig } from './registry';
import type { CampaignConfig } from './types';

export interface CampaignHandle {
	canDisplay: boolean;
	onSeen: () => void;
	onXClose: () => void;
	onDismiss: () => void;
}

/**
 * Internal variant taking an explicit config and manager — the seam the unit
 * tests use to run injected test campaigns against a fresh manager.
 */
export const useCampaignForConfig = (
	config: CampaignConfig,
	isEligible: boolean,
	manager: CampaignManager = campaignManager
): CampaignHandle => {
	const dispatch = useAppDispatch();
	const isMeLoaded = useSelector(selectIsMeLoaded);
	const impressions = useSelector(selectCampaignImpressions);
	const impression = impressions[config.id];

	const [grantedId, setGrantedId] = useState<string | null>(manager.getGrantedId());
	useEffect(() => {
		setGrantedId(manager.getGrantedId());
		return manager.subscribe(() => setGrantedId(manager.getGrantedId()));
	}, [manager]);

	const impressionRef = useRef(impression);
	impressionRef.current = impression;

	useEffect(() => {
		if (!isEligible || !isMeLoaded) {
			return;
		}
		manager.request(config, impressionRef.current);
		return () => {
			manager.withdraw(config.id);
		};
	}, [manager, config, isEligible, isMeLoaded]);

	// Refresh the registered candidate when the impression record changes (e.g.
	// a dismiss landed) WITHOUT withdrawing — a withdraw would drop an active
	// grant and close the campaign mid-display. `request` updates in place.
	useEffect(() => {
		if (isEligible && isMeLoaded) {
			manager.request(config, impression);
		}
	}, [manager, config, isEligible, isMeLoaded, impression]);

	const canDisplay = grantedId === config.id;

	// Idempotent per display: re-arms only after the grant was lost, so effects
	// re-running while the campaign stays visible can't double-record.
	const seenRef = useRef(false);
	useEffect(() => {
		if (!canDisplay) {
			seenRef.current = false;
		}
	}, [canDisplay]);

	const onSeen = useCallback(() => {
		if (!canDisplay || seenRef.current) {
			return;
		}
		seenRef.current = true;
		manager.recordSeen(config.id);
		dispatch(recordCampaignImpression({ campaignId: config.id, action: 'display' }));
		ampliTrackMessageDisplayedEvent(config.trackingCampaign, config.messageType);
		if (config.dismissOnDisplay) {
			dispatch(recordCampaignImpression({ campaignId: config.id, action: 'dismiss' }));
		}
	}, [canDisplay, config, dispatch, manager]);

	const onXClose = useCallback(() => {
		manager.snooze(config.id);
	}, [manager, config.id]);

	const onDismiss = useCallback(() => {
		dispatch(recordCampaignImpression({ campaignId: config.id, action: 'dismiss' }));
	}, [dispatch, config.id]);

	return { canDisplay, onSeen, onXClose, onDismiss };
};

/**
 * Campaign gate for proactive surfaces. The component computes its own
 * placement eligibility (route scoping, app-state gates) and passes it in; the
 * module owns the session mutex, priority, display limits, cooldown, dismissal
 * state, and display tracking. See `apps/docs/content/guides/proactive-campaigns.md`.
 */
export const useCampaign = (id: CampaignId, isEligible: boolean): CampaignHandle =>
	useCampaignForConfig(getCampaignConfig(id), isEligible);
